/**
 * CaseFlow+AI - Design Tokens
 * Phase 5B: Light Mode + Design System Hardening
 * 
 * These tokens ensure consistent theming across light and dark modes.
 * All components should use these tokens instead of hardcoded colors.
 */

export const themeTokens = {
  colors: {
    // Backgrounds
    background: {
      light: '#ffffff',
      dark: '#0a0a0b',
    },
    surface: {
      light: '#f9fafb',
      dark: '#0f0f11',
    },
    elevated: {
      light: '#ffffff',
      dark: '#18181b',
    },
    
    // Borders
    border: {
      light: '#e5e7eb',
      dark: 'rgba(255, 255, 255, 0.1)',
    },
    borderSubtle: {
      light: '#f3f4f6',
      dark: 'rgba(255, 255, 255, 0.05)',
    },
    
    // Text
    text: {
      primary: {
        light: '#111827',
        dark: '#ffffff',
      },
      secondary: {
        light: '#6b7280',
        dark: '#9ca3af',
      },
      muted: {
        light: '#9ca3af',
        dark: '#6b7280',
      },
    },
    
    // Semantic colors
    accent: {
      light: '#6366f1',
      dark: '#818cf8',
    },
    critical: {
      light: '#dc2626',
      dark: '#f87171',
    },
    warning: {
      light: '#d97706',
      dark: '#fbbf24',
    },
    success: {
      light: '#16a34a',
      dark: '#4ade80',
    },
    info: {
      light: '#2563eb',
      dark: '#60a5fa',
    },
    
    // Interactive states
    hover: {
      light: 'rgba(0, 0, 0, 0.04)',
      dark: 'rgba(255, 255, 255, 0.05)',
    },
    active: {
      light: 'rgba(0, 0, 0, 0.08)',
      dark: 'rgba(255, 255, 255, 0.1)',
    },
  },
  
  // Tailwind CSS class mappings for dark mode
  darkMode: {
    background: 'dark:bg-[#0a0a0b]',
    surface: 'dark:bg-[#0f0f11]',
    elevated: 'dark:bg-[#18181b]',
    border: 'dark:border-white/10',
    borderSubtle: 'dark:border-white/5',
    textPrimary: 'dark:text-white',
    textSecondary: 'dark:text-gray-400',
    textMuted: 'dark:text-gray-500',
  },
} as const;

// Utility function to get appropriate color class
export function getThemeColor(
  colorPath: keyof typeof themeTokens.colors,
  theme: 'light' | 'dark'
): string {
  const color = themeTokens.colors[colorPath];
  if (typeof color === 'object' && 'light' in color && 'dark' in color) {
    return color[theme];
  }
  return color.light;
}

// CSS custom properties for use in inline styles
export const cssVariables = {
  light: {
    '--color-background': '#ffffff',
    '--color-surface': '#f9fafb',
    '--color-elevated': '#ffffff',
    '--color-border': '#e5e7eb',
    '--color-text-primary': '#111827',
    '--color-text-secondary': '#6b7280',
    '--color-accent': '#6366f1',
    '--color-critical': '#dc2626',
    '--color-warning': '#d97706',
    '--color-success': '#16a34a',
  },
  dark: {
    '--color-background': '#0a0a0b',
    '--color-surface': '#0f0f11',
    '--color-elevated': '#18181b',
    '--color-border': 'rgba(255, 255, 255, 0.1)',
    '--color-text-primary': '#ffffff',
    '--color-text-secondary': '#9ca3af',
    '--color-accent': '#818cf8',
    '--color-critical': '#f87171',
    '--color-warning': '#fbbf24',
    '--color-success': '#4ade80',
  },
};

export type Theme = 'light' | 'dark';
export type ColorTone = 'default' | 'green' | 'amber' | 'red' | 'blue' | 'purple';

export const toneColors: Record<ColorTone, { light: string; dark: string }> = {
  default: {
    light: 'bg-gray-100 text-gray-700',
    dark: 'bg-white/10 text-gray-200',
  },
  green: {
    light: 'bg-emerald-100 text-emerald-700',
    dark: 'bg-emerald-500/15 text-emerald-300',
  },
  amber: {
    light: 'bg-amber-100 text-amber-700',
    dark: 'bg-amber-500/15 text-amber-300',
  },
  red: {
    light: 'bg-rose-100 text-rose-700',
    dark: 'bg-rose-500/15 text-rose-300',
  },
  blue: {
    light: 'bg-blue-100 text-blue-700',
    dark: 'bg-blue-500/15 text-blue-300',
  },
  purple: {
    light: 'bg-violet-100 text-violet-700',
    dark: 'bg-violet-500/15 text-violet-300',
  },
};
